import React from "react";
import ContactUs from "../../../components/ContactUs.js";
import { Helmet } from "react-helmet-async";

const services = [
  {
    logo_images: "../assets/icons/elk/service-1.png",
    title: "Log Collection",
    description:
      "Instrument applications, containers, and infrastructure with Beats, Fluentd/Fluent Bit, and custom shippers to collect structured and unstructured logs at scale.",
  },
  {
    logo_images: "../assets/icons/elk/service-2.png",
    title: "Log Processing & Enrichment",
    description:
      "Build Logstash or Fluent pipelines for parsing, enrichment, masking, and routing with dynamic pipelines and schema governance.",
  },
  {
    logo_images: "../assets/icons/elk/service-3.png",
    title: "Centralized Storage",
    description:
      "Design Elasticsearch or OpenSearch clusters with hot-warm-cold tiers, ILM policies, and snapshot strategies to optimize retention and cost.",
  },
  {
    logo_images: "../assets/icons/elk/service-4.png",
    title: "Dashboards & Analytics",
    description:
      "Create Kibana dashboards, Canvas workpads, and discovery views for troubleshooting, compliance, and business analytics with RBAC and spaces.",
  },
  {
    logo_images: "../assets/icons/elk/service-5.png",
    title: "Alerting & Incident Automation",
    description:
      "Configure Watcher, ElastAlert, or Alertmanager integrations to trigger incidents, correlate events, and feed SOAR/ITSM platforms.",
  },
];

const benefits = [
  "Single source of truth for logs across applications, infrastructure, and network devices",
  "Accelerated troubleshooting with powerful search, visualization, and correlation",
  "Compliance-ready retention with auditable access controls and masking",
  "Cost control through tiered storage, compression, and aggregation",
  "Proactive alerting that detects anomalies before they become incidents",
  "Improved collaboration between Dev, Sec, and Ops through shared dashboards",
];

const expertise = [
  "Elastic Certified Engineers and observability specialists",
  "Experience migrating from Splunk, Graylog, and proprietary SIEM platforms",
  "Fluentd/Fluent Bit, Filebeat, Winlogbeat, and custom collector expertise",
  "Security controls including TLS, SSO, encryption at rest, and audit logging",
  "Integration with monitoring, APM, and SIEM ecosystems",
  "Infrastructure-as-code deployment via Terraform, Helm, and Ansible",
];

const useCases = [
  {
    title: "Enterprise Logging Platform",
    description:
      "Implemented an ELK stack processing 8TB/day with hot-warm architecture, RBAC, and automated alerts, reducing MTTR by 40%.",
  },
  {
    title: "Container Log Aggregation",
    description:
      "Deployed Fluent Bit DaemonSets and Kibana dashboards for Kubernetes clusters, providing pod-level insights and compliance evidence.",
  },
  {
    title: "Security Operations Analytics",
    description:
      "Integrated EFK with SOC tools, enabling correlation across firewalls, IDS, and application logs to detect anomalies in near real-time.",
  },
  {
    title: "Multi-Cloud Edge Logging",
    description:
      "Built federated logging pipelines across AWS, Azure, and on-prem with centralized Kibana views and data lifecycle management.",
  },
];

const reasons = [
  "Reference architectures for HA, DR, and multi-region ELK/EFK deployments",
  "Governance accelerators covering schema, tagging, and retention policies",
  "Security-by-design with data masking, tokenization, and zero-trust access",
  "Performance tuning expertise to handle petabyte-scale workloads",
  "Enablement programs and runbooks for DevSecOps teams",
  "Managed logging services with 24/7 monitoring and incident response",
];

const scrollToContact = () => {
  document
    .getElementById("contact-section")
    ?.scrollIntoView({ behavior: "smooth" });
};

const LoggingStack = () => (
  <>
    <Helmet>
      <meta
        name="description"
        content="Ayanshtech Solutions — ELK / EFK logging stack services for log collection, processing, Kibana dashboards, and centralized logging."
      />
      <meta name="copyright" content="codeglim" />
      <meta
        property="og:title"
        content="ELK / EFK Logging Stack | Centralized Logging Services | Ayanshtech Solutions"
      />
      <meta property="og:site_name" content="Ayanshtech Solutions" />
      <meta
        property="og:url"
        content="http://ayanshtechnology.com/devops-logging"
      />
      <meta
        property="og:description"
        content="Centralized logging solutions with Elasticsearch, Logstash, Kibana, and Fluentd."
      />
      <meta property="og:type" content="website" />
      <meta
        property="og:image"
        content="http://ayanshtechnology.com/assets/logo.png"
      />
      <link
        rel="canonical"
        href="http://ayanshtechnology.com/devops-logging"
      />
      <title>
        ELK / EFK Logging Stack | Centralized Logging Services | Ayanshtech
        Solutions
      </title>
    </Helmet>

    <div className="flex flex-col items-center justify-center mb-6 p-6">
      <section className="py-5 w-full">
        <div className="flex flex-col lg:flex-row items-center lg:space-x-16">
          <div className="lg:w-3/4 lg:text-left p-4">
            <h1 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
              ELK / EFK Logging Stack
            </h1>
            <p className="sm:text-base md:text-lg lg:text-lg mb-3">
              Gain end-to-end visibility into logs from every layer of your stack.
              We design and operate ELK/EFK platforms that collect, process, and
              surface actionable insights for DevSecOps teams.
            </p>
            <button
              onClick={scrollToContact}
              className="bg-green-600 text-white hover:bg-yellow-500 hover:text-green-700 hover:border hover:border-yellow-500 font-semibold py-2 px-4 rounded-full mt-6"
            >
              Build Centralized Logging
            </button>
          </div>
          <div className="flex w-full md:w-2/3  justify-center mb-12 lg:mb-0">
            <img
              src="../assets/images/devops-pages/EFK-banner.png"
              alt="Logging Stack"
              className="w-full h-auto sm:w-3/4 md:w-2/3 lg:w-full object-contain"
            />
          </div>
        </div>
      </section>

      {/* <section className="mb-5 mt-10 p-4 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          About ELK / EFK
        </h2>
        <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
          Elasticsearch, Logstash/Fluentd, and Kibana/OpenSearch Dashboards form
          the cornerstone of centralized logging. They gather logs from diverse
          systems, normalize data, and deliver powerful search and analytics for
          engineering, security, and compliance teams.
        </p>
      </section> */}

      <section className="text-center mb-5 mt-12 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Logging Services
        </h2>
        <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
          Comprehensive logging offerings covering ingestion, processing,
          analytics, and alerting.
        </p>
      </section>
      <div className="rounded-3xl p-6 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <div
              key={service.title}
              className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
             
            >
              <div className="flex items-center gap-2">
                <div className="w-[60px] h-[60px]  ">
                  <img src={service.logo_images} className="w-full" alt="" />
                </div>
                <h3 className="text-lg font-bold">{service.title}</h3>
              </div>
              <p className="text-lg text-gray-700">{service.description}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="text-center mb-5 mt-20 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Benefits of Centralized Logging
        </h2>
      </section>
      <div className="p-4 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit) => (
            <div
              key={benefit}
              className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
            >
              <div className="h-3 w-3 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
              <p className="text-lg text-gray-700">{benefit}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="p-6 sm:px-8 md:px-12 lg:px-16 w-full mt-10">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Our Logging Expertise
          </h2>
        </div>
        <div className="flex justify-around item-center flex-col md:flex-row ">
        <ul className="list-disc pl-8 sm:text-base md:text-lg lg:text-xl max-w-6xl my-auto space-y-3 text-gray-700">
          {expertise.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <div className="w-full md:w-1/2 lg:w-[25%]">
          <img src="../assets/images/devops-pages/Elk-image.webp" alt="" />
        </div>
        </div>
      </section>

      <section className="text-center mb-5 mt-10 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Use Cases
        </h2>
      </section>
      <div className="rounded-3xl p-6 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {useCases.map((useCase) => (
            <div
              key={useCase.title}
              className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none "
             
            >
              <h3 className="text-lg font-bold text-gray-900">
                {useCase.title}
              </h3>
              <p className="text-lg text-gray-700">{useCase.description}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="text-center mb-5 mt-10 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Why Choose Ayanshtech
        </h2>
      </section>
      <div className="p-6 sm:p-8 md:p-12 lg:p-16 w-full ">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {reasons.map((reason) => (
            <div
              key={reason}
              className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none "
            >
              <div className="h-3 w-3 rounded-full bg-green-600 mt-2 flex-shrink-0" />
              <p className="text-lg text-gray-700">{reason}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <button
            onClick={scrollToContact}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300"
          >
            Centralize Your Logging
          </button>
        </div>
      </div>

      <div id="contact-section" className="w-full mt-12">
        <ContactUs />
      </div>
    </div>
  </>
);

export default LoggingStack;

